import { z } from 'zod';

/**
 * GM preference profile — two-layer style/personalization model (#250,
 * `04-llm-role.md` §16). Decomposed child 224.1a of #224.
 *
 * The profile shapes LLM generation toward the GM's style. It is built from
 * TWO layers that compose into one block fed to the prompt composer alongside
 * the existing two-layer prompt system (D13 — `ai_prompt_extensions`):
 *
 *   1. **Explicit layer** — GM-authored knobs (tone, content boundaries,
 *      pacing, naming, vocabulary, style references, free-form notes). This is
 *      a config-bucket SETTING (`gm_pref_profile` in `config-ops.ts`):
 *      last-writer-wins, GM-authored, no reveal/validity/belief. A preference
 *      is a setting, never a fact — so it is export-clean and NEVER touches
 *      canon (ADR-0004 three-bucket model; "the LLM never writes canon").
 *
 *   2. **Inferred layer** — signals-derived entries (accepted/edited/rejected
 *      drafts, explicit GM corrections — §16.2). This is a SEPARATE,
 *      rebuildable derived store (`InferredPrefProfile`) — NEVER authoritative,
 *      reconstructable at any time from the signal log. It is gated behind an
 *      **opt-in** (`gm_pref_inference_enabled`, default OFF — §16.3,
 *      "AI is always optional"): the composed profile includes inferred
 *      entries ONLY when the GM has explicitly enabled inference.
 *
 * This module is the data-model + composition + render layer. Signal *capture*
 * (224.1b) and the challenge-mode / drift UI (224.1c) are separate follow-ups;
 * the shapes here are scaffolded so they slot in (each inferred entry carries
 * provenance — the originating signal + when + a freeze flag — per §16.4).
 *
 * Profile text is internal model instruction, NOT user-facing UI copy: like
 * `ai_prompt_extensions`, it does NOT flow through the i18n message function
 * (that governs UI strings). The Settings UI labels around these fields DO go
 * through i18n; the field *values* are GM-authored free text fed to the model.
 */

// ─── Explicit layer (GM-authored config setting) ───────────────────────

/**
 * The five structured style dimensions (§16.1 Layer 1). Ordered for
 * deterministic prompt rendering. These keys double as the inferred-layer
 * dimension keys so the two layers compose dimension-by-dimension.
 */
export const GM_PREF_DIMENSIONS = [
  'tone',
  'naming',
  'vocabulary',
  'pacing',
  'style_references',
] as const;
export type GmPrefDimension = (typeof GM_PREF_DIMENSIONS)[number];

/**
 * The explicit GM-authored preference profile (§16.1 Layer 1). Every field is
 * optional — an unset field contributes nothing to the composed block, so a
 * brand-new campaign with no profile yields an empty (inert) block and AI
 * stays fully functional with the admin templates alone. `.strict()` so an
 * unknown dimension is rejected (the composer routes by dimension; a stray key
 * would be a silent no-op).
 *
 * Each dimension is a free-text instruction string (not a list) for the
 * explicit layer — the GM writes prose ("terse and evocative; avoid purple
 * descriptions"). The inferred layer is where per-entry provenance lives.
 * `content_boundaries` is a dedicated field (a hard line the model must respect
 * — never inferred, GM-authored only) kept separate from the soft style knobs.
 */
export const GmPrefProfileSchema = z
  .object({
    /** Tone: formal / terse / evocative / clinical (§16.1). Free text. */
    tone: z.string().optional(),
    /** Naming conventions: preferred patterns, cultural roots, forbidden patterns. */
    naming: z.string().optional(),
    /** Vocabulary: words to favor, words to avoid. */
    vocabulary: z.string().optional(),
    /** Pacing: description density, dialogue length, scene rhythm. */
    pacing: z.string().optional(),
    /** Style references: named authors / settings / aesthetics the GM enjoys. */
    style_references: z.string().optional(),
    /**
     * Content boundaries — a hard line the model must respect (e.g. "no graphic
     * violence", "fade to black on intimacy"). GM-authored ONLY; never inferred
     * (a boundary is a deliberate constraint, not a learned habit) and rendered
     * as an explicit instruction the model must not cross.
     */
    content_boundaries: z.string().optional(),
    /** Free-form notes: "morally-gray villains", "low-magic", "avoid D&D tropes". */
    free_form: z.string().optional(),
  })
  .strict();
export type GmPrefProfile = z.infer<typeof GmPrefProfileSchema>;

/** An empty explicit profile (the no-preferences default). */
export const EMPTY_GM_PREF_PROFILE: GmPrefProfile = {};

// ─── Inferred layer (rebuildable derived store — NEVER authoritative) ──

/**
 * The signal that produced an inferred entry (§16.2). Used as provenance on
 * each entry ("added by what signal" — §16.4) and to weight inference in
 * 224.1b. Kept as a closed enum so the UI can label each provenance
 * deterministically.
 */
export const InferenceSignalSchema = z.enum([
  'accepted_as_is', // accepted-as-is draft → positive signal
  'edited_before_accept', // the diff is the signal (what they edited away from)
  'rejected', // rejected draft with optional reason → negative signal
  'explicit_correction', // "call this a 'binder' not a 'warlock'"
]);
export type InferenceSignal = z.infer<typeof InferenceSignalSchema>;

/**
 * One inferred preference entry (§16.4). Carries the dimension it belongs to,
 * the inferred text, and full provenance — the originating signal, when it was
 * added, and a `frozen` flag (immune to further inference adjustment). The
 * `frozen` + `added_at` + `signal` triple is what the inspectability /
 * challenge-mode UI (224.1c) renders; it is scaffolded here so capture (224.1b)
 * has a stable target.
 */
export const InferredPrefEntrySchema = z
  .object({
    /** Stable entry id (minted at capture time). */
    id: z.string().min(1),
    /** Which style dimension this entry refines. */
    dimension: z.enum(GM_PREF_DIMENSIONS),
    /** The inferred instruction text. */
    text: z.string().min(1),
    /** The signal that produced this entry. */
    signal: InferenceSignalSchema,
    /** ISO-8601 timestamp the entry was added (string — no `Date` in Model). */
    added_at: z.string().min(1),
    /** GM-frozen: preserved across re-inference and per-dimension reset (§16.4). */
    frozen: z.boolean(),
  })
  .strict();
export type InferredPrefEntry = z.infer<typeof InferredPrefEntrySchema>;

/**
 * The inferred-layer store: a flat list of entries (§16.1 Layer 1 inference).
 * Deliberately a list, NOT a config value — it is a REBUILDABLE derived store,
 * reconstructed from the signal log (224.1b), never written to the canonical
 * config bucket and never authoritative. The opt-in gate
 * (`gm_pref_inference_enabled`) governs whether it is built AND whether it is
 * composed into the prompt; see `composeGmPrefProfile`.
 */
export const InferredPrefProfileSchema = z
  .object({
    entries: z.array(InferredPrefEntrySchema),
  })
  .strict();
export type InferredPrefProfile = z.infer<typeof InferredPrefProfileSchema>;

/** An empty inferred store (the never-inferred / inference-off default). */
export const EMPTY_INFERRED_PREF_PROFILE: InferredPrefProfile = { entries: [] };

// ─── Composition (explicit + opt-in-gated inferred → one block) ────────

/**
 * The composed profile ready for the prompt composer. `explicit` is always the
 * GM-authored config setting; `inferred` is the entries that survived the
 * opt-in gate (empty unless inference is enabled); `inference_active` records
 * whether the gate was open (so the UI / trace can show "inference contributed
 * to this call" — §16.4 "why did you suggest this?").
 */
export interface ComposedPrefProfile {
  explicit: GmPrefProfile;
  inferred: readonly InferredPrefEntry[];
  /** True iff the opt-in gate was open (inference enabled). */
  inference_active: boolean;
}

/**
 * Compose the two layers into one profile for the prompt composer.
 *
 * **The opt-in gate (the load-bearing rule — §16.3):** the inferred layer is
 * included ONLY when `inferenceEnabled === true`. With inference OFF (the
 * default), the composed profile is the explicit layer alone — inferred entries
 * are dropped entirely, regardless of what the derived store holds. This is the
 * single chokepoint enforcing "inference is opt-in, off by default": even if a
 * stale inferred store is passed in, a `false` gate yields zero inferred
 * contribution.
 *
 * Pure and total — any input (including an absent explicit profile) yields a
 * well-formed `ComposedPrefProfile`. Never throws; never reads a store.
 */
export function composeGmPrefProfile(args: {
  explicit: GmPrefProfile | undefined;
  inferred: InferredPrefProfile | undefined;
  inferenceEnabled: boolean;
}): ComposedPrefProfile {
  const explicit = args.explicit ?? EMPTY_GM_PREF_PROFILE;
  // The gate: inferred entries flow through ONLY when explicitly enabled.
  const inferred =
    args.inferenceEnabled && args.inferred ? args.inferred.entries : [];
  return {
    explicit,
    inferred,
    inference_active: args.inferenceEnabled,
  };
}

/**
 * Default for the opt-in gate: inference is OFF until the GM enables it
 * (§16.3). Exported so the config-key default and every reader agree on a
 * single source of truth.
 */
export const DEFAULT_GM_PREF_INFERENCE_ENABLED = false;

// ─── Render (composed profile → prompt block) ──────────────────────────

/** Human-readable dimension labels for the rendered prompt (model-facing). */
const DIMENSION_PROMPT_LABEL: Record<GmPrefDimension, string> = {
  tone: 'Tone',
  naming: 'Naming conventions',
  vocabulary: 'Vocabulary',
  pacing: 'Pacing',
  style_references: 'Style references',
};

/**
 * Render the composed profile to a prompt block (model instruction text). The
 * block is appended to the system prompt by the composer. Returns `''` when the
 * profile contributes nothing (no explicit fields AND no gated inferred
 * entries), so the composer can drop it cleanly.
 *
 * Ordering is deterministic: explicit dimensions (in `GM_PREF_DIMENSIONS`
 * order) then the gated inferred entries grouped by dimension, then content
 * boundaries, then free-form notes. Inferred entries are clearly labelled
 * `(learned)` so the block stays inspectable.
 */
export function renderGmPrefProfile(composed: ComposedPrefProfile): string {
  const lines: string[] = [];
  const { explicit, inferred } = composed;

  for (const dim of GM_PREF_DIMENSIONS) {
    const v = explicit[dim];
    if (typeof v === 'string' && v.trim() !== '') {
      lines.push(`- ${DIMENSION_PROMPT_LABEL[dim]}: ${v.trim()}`);
    }
    // Inferred entries (already opt-in-gated by composition) for this
    // dimension, in dimension order, each marked `(learned)`.
    for (const entry of inferred) {
      if (entry.dimension !== dim) continue;
      if (entry.text.trim() === '') continue;
      lines.push(`- ${DIMENSION_PROMPT_LABEL[dim]} (learned): ${entry.text.trim()}`);
    }
  }

  const boundaries = explicit.content_boundaries;
  const freeForm = explicit.free_form;

  const styleBlock =
    lines.length > 0
      ? `GM style preferences (match these unless told otherwise):\n${lines.join('\n')}`
      : '';
  const boundariesBlock =
    typeof boundaries === 'string' && boundaries.trim() !== ''
      ? `Content boundaries (must respect): ${boundaries.trim()}`
      : '';
  const notesBlock =
    typeof freeForm === 'string' && freeForm.trim() !== ''
      ? `GM notes: ${freeForm.trim()}`
      : '';

  return [styleBlock, boundariesBlock, notesBlock].filter((b) => b !== '').join('\n\n');
}
